import {useCallback, useEffect, useMemo, useRef, type SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import useShow from '../../../hooks/useShow';
import useRestartXHR from '../../../hooks/useRestartXHR';
import setGlobalState, {resetGlobalState} from '../../../redux/setGlobalState';
import {loadAiAgentMeta} from '../../../redux/aiAgent/aiAgentActions';
import {XHR_LOADING_STATE} from '../../../assets/constants/constants';
import type {TXHR} from '../../../assets/constants/constansts.type';
import type {TState} from '../../../redux/store.type';
import {createAiAgentSubmitHandler} from '../utils/aiAgentSubmit';
import {clearHeldProposal} from '../utils/agentProposalHold';
import {clearAgentFeedbackRequest} from '../utils/agentFeedbackRequest';
import {closeAgentPanel} from '../utils/agentPanelState';
import {startAgentChatSession} from '../utils/agentChatSession';
import {buildDroppedFieldsNotice} from '../utils/droppedFieldsNotice';
import {getProposalConfirmedKey} from '../utils/proposalConfirmedText';
import {useAiAgentAsk} from './useAiAgentAsk';

export function useAiAgentPanel() {
    const show = useShow();
    const {t} = useTranslation('aiAgent');
    const navigate = useNavigate();
    const isOpen = useSelector((s: TState) => s.aiAgent?.isOpen);
    // Trigger only — loadAiAgentMeta resolves the pid itself (URL first, like the chat).
    const agentContextPid = useSelector((s: TState) => s.account?.pid ?? s.event?.pid ?? null);
    const sendMessageXHR = useSelector((s: TState) => s.aiAgent?.sendMessageXHR);
    const executeProposalXHR = useSelector((s: TState) => s.aiAgent?.executeProposalXHR);
    const pendingProposal = useSelector((s: TState) => s.aiAgent?.pendingProposal);
    const pendingFeedback = useSelector((s: TState) => s.aiAgent?.pendingFeedback);
    const feedbackXHR = useSelector((s: TState) => s.aiAgent?.feedbackXHR);

    const turnXHR: TXHR = executeProposalXHR === XHR_LOADING_STATE
        ? XHR_LOADING_STATE
        : sendMessageXHR ?? null;

    const submitHandler = useMemo(
        () => createAiAgentSubmitHandler(navigate, {
            fileTooLarge: t('fileTooLarge'),
            fileUnsupported: t('fileUnsupported'),
            actionFailed: t('actionFailed'),
            createdOpenFailed: t('createdOpenFailed'),
            proposalConfirmed: (intent: string) => t(getProposalConfirmedKey(intent)),
            proposalUpdated: t('proposalUpdated'),
            proposalAlreadyCreated: t('proposalAlreadyCreated'),
            proposalOtherOwner: t('proposalOtherOwner'),
            droppedNotice: (fields: string[]) => buildDroppedFieldsNotice(fields, t),
        }),
        [navigate, t],
    );
    const {pendingAsk, clearAsk, handleSubmit} = useAiAgentAsk(submitHandler);
    useRestartXHR(sendMessageXHR, 'aiAgent.sendMessageXHR');
    useRestartXHR(executeProposalXHR, 'aiAgent.executeProposalXHR');
    useRestartXHR(feedbackXHR, 'aiAgent.feedbackXHR');

    // useShow returns a fresh object every render, so `show` itself must stay out of the
    // dependency list — a ref keeps the effect honest without re-running it each render.
    const showRef = useRef(show);
    showRef.current = show;
    const isPanelOpen = show.isOpen;
    const isPanelVisible = !!show.showClass;

    useEffect(() => {
        if(isOpen && !isPanelOpen) {
            showRef.current.open();
            return;
        }
        if(!isOpen && isPanelVisible) {
            void showRef.current.close();
        }
    }, [isOpen, isPanelOpen, isPanelVisible]);

    // The suggestions and confirm labels are built from the server's catalog — prefetched as
    // soon as the agent mounts and the package is known, so the chips are already there when
    // the panel opens. Re-fires on package change and on open (the action skips repeats).
    useEffect(() => {
        void loadAiAgentMeta();
    }, [isOpen, agentContextPid]);

    const openAgent = useCallback(() => {
        setGlobalState('aiAgent.isOpen', true);
        show.open();
    }, [show]);

    const closeAgent = useCallback(async (e?: Event | SyntheticEvent) => {
        closeAgentPanel();
        // A ticket whose page never mounted must not pop up on a later visit.
        resetGlobalState('aiAgent.pendingTicketTid');
        await show.close(e);
    }, [show]);

    const handleReset = useCallback(() => {
        // First: a turn still running belongs to the chat being left, and everything cleared
        // below must stay cleared when that turn finishes.
        startAgentChatSession();
        clearAsk();
        clearHeldProposal();
        clearAgentFeedbackRequest();
        setGlobalState('aiAgent.sessionDiscriminator', null);
        setGlobalState('aiAgent.sessionIntent', null);
        setGlobalState('aiAgent.chatLang', null);
        setGlobalState('aiAgent.activeFormId', null);
        setGlobalState('aiAgent.createdDraftFields', null);
        setGlobalState('aiAgent.createdDraftEid', null);
        setGlobalState('aiAgent.currentFormValues', null);
        resetGlobalState('aiAgent.pendingTicketTid');
    }, [clearAsk]);

    const agentShow = useMemo(() => ({
        ...show,
        open: openAgent,
        close: closeAgent,
    }), [show, openAgent, closeAgent]);

    return {
        t,
        agentShow,
        openAgent,
        handleReset,
        handleSubmit,
        pendingAsk,
        clearAsk,
        pendingProposal: pendingProposal ?? null,
        pendingFeedback: pendingFeedback ?? null,
        turnXHR,
    };
}
